import { useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button, buttonVariants } from '@/components/ui/button';
import { useT } from '@/i18n/use-t';
import { cn } from '@/lib/utils';

type OneTimePinModalProps = {
  open: boolean;
  /** Cleartext PIN handed over by the server — visible exactly once. */
  pin: string;
  cashierLabel: string;
  /** Copy override for the title — defaults to "PIN provisioned". */
  title?: string;
  /** Called after the user has acknowledged + dismissed the modal. */
  onAcknowledge: () => void;
};

/**
 * One-time cleartext-PIN display modal. Spec §"Admin UI surface"
 * gates the dismiss with an AlertDialog because losing the cleartext
 * before handover to the cashier requires a re-provision.
 *
 * No Cancel button — once the dialog opens, the operator MUST
 * acknowledge they've copied the PIN. The only safe path forward is
 * to confirm. (Re-provisioning is a 1-click flow via "Rotate PIN" if
 * they botch it.)
 */
export function OneTimePinModal({
  open,
  pin,
  cashierLabel,
  title,
  onAcknowledge,
}: OneTimePinModalProps) {
  const t = useT();
  const [copied, setCopied] = useState(false);
  const heading = title ?? t('employees.pin.provisionedTitle', 'PIN provisioned');

  async function copyPin(): Promise<void> {
    try {
      await navigator.clipboard.writeText(pin);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard API blocked (no HTTPS / no permission). Fall through —
      // the user can still read + retype the PIN from the on-screen box.
    }
  }

  return (
    <AlertDialog open={open}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{heading}</AlertDialogTitle>
          <AlertDialogDescription>
            {t('employees.pin.modalBody1', 'This is the new PIN for')}{' '}
            <strong>{cashierLabel}</strong>
            {t('employees.pin.modalBody2', '. It will')}{' '}
            <strong>{t('employees.pin.not', 'NOT')}</strong>{' '}
            {t(
              'employees.pin.modalBody3',
              'be shown again. Hand it to the cashier now. If you lose it, you must rotate the PIN to issue a new one.',
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="my-2 flex flex-col items-center gap-3">
          <div className="rounded-card border border-border bg-secondary px-6 py-4 font-mono text-3xl tracking-widest text-primary">
            {pin}
          </div>
          <Button type="button" variant="outline" size="sm" onClick={copyPin}>
            {copied
              ? t('employees.pin.copied', 'Copied!')
              : t('employees.pin.copy', 'Copy to clipboard')}
          </Button>
        </div>

        <AlertDialogFooter>
          <AlertDialogAction
            className={cn(buttonVariants({ variant: 'default' }))}
            onClick={() => {
              setCopied(false);
              onAcknowledge();
            }}
          >
            {t('employees.pin.acknowledge', "I've handed the PIN to the cashier")}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
